import apiClient, { handleApiResponse } from './client';
import type {
  NIBSubmission,
  NIBSubmissionDetail,
  NIBSubmissionList,
  NIBSubmissionCreateRequest,
  NIBSubmissionFilters,
  NIBEmployeeRecord,
  NIBRecordCreateRequest,
  NIBImportResult,
  NIBMergeResult,
  NIBNewHire,
  NIBValidationResult,
  NIBContributionPreview,
  NIBWagePreview,
  NIBContributionPreviewRequest,
  NIBWagePreviewRequest,
  NIBSourceSystem,
  NIBPayIndicator,
  ApiResponse,
} from '@/types';

export const nibApi = {
  // ==================== Submissions ====================

  // Create a new NIB submission
  createSubmission: async (
    data: NIBSubmissionCreateRequest
  ): Promise<NIBSubmission> => {
    const response = await apiClient.post<ApiResponse<NIBSubmission>>(
      '/v1/nib/submissions',
      data
    );
    return handleApiResponse(response);
  },

  // Get list of submissions with pagination and filters
  getSubmissions: async (
    params: NIBSubmissionFilters = {}
  ): Promise<NIBSubmissionList> => {
    const response = await apiClient.get<ApiResponse<NIBSubmissionList>>(
      '/v1/nib/submissions',
      { params }
    );
    return handleApiResponse(response);
  },

  // Get a single submission with all records
  getSubmissionById: async (
    submissionId: string
  ): Promise<NIBSubmissionDetail> => {
    const response = await apiClient.get<ApiResponse<NIBSubmissionDetail>>(
      `/v1/nib/submissions/${submissionId}`
    );
    return handleApiResponse(response);
  },

  // Delete a submission (soft delete)
  deleteSubmission: async (submissionId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/v1/nib/submissions/${submissionId}`
    );
    return handleApiResponse(response);
  },

  // ==================== Records ====================

  // Add a single employee record to a submission
  addRecord: async (
    submissionId: string,
    data: NIBRecordCreateRequest
  ): Promise<NIBEmployeeRecord> => {
    const response = await apiClient.post<ApiResponse<NIBEmployeeRecord>>(
      `/v1/nib/submissions/${submissionId}/records`,
      data
    );
    return handleApiResponse(response);
  },

  // Update an existing employee record
  updateRecord: async (
    submissionId: string,
    recordId: string,
    data: Partial<NIBRecordCreateRequest>
  ): Promise<NIBEmployeeRecord> => {
    const response = await apiClient.put<ApiResponse<NIBEmployeeRecord>>(
      `/v1/nib/submissions/${submissionId}/records/${recordId}`,
      data
    );
    return handleApiResponse(response);
  },

  // Delete an employee record
  deleteRecord: async (
    submissionId: string,
    recordId: string
  ): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/v1/nib/submissions/${submissionId}/records/${recordId}`
    );
    return handleApiResponse(response);
  },

  // Import records from a payroll file (QuickBooks/ADP)
  importRecords: async (
    submissionId: string,
    file: File,
    sourceSystem: NIBSourceSystem,
    payIndicator?: NIBPayIndicator
  ): Promise<NIBImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_system', sourceSystem);
    if (payIndicator) {
      formData.append('pay_indicator', payIndicator);
    }

    const response = await apiClient.post<ApiResponse<NIBImportResult>>(
      `/v1/nib/submissions/${submissionId}/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return handleApiResponse(response);
  },

  // Smart merge payroll file into submission with existing roster records
  mergePayroll: async (
    submissionId: string,
    file: File,
    sourceSystem: NIBSourceSystem,
    payIndicator?: NIBPayIndicator
  ): Promise<NIBMergeResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_system', sourceSystem);
    if (payIndicator) {
      formData.append('pay_indicator', payIndicator);
    }

    const response = await apiClient.post<ApiResponse<NIBMergeResult>>(
      `/v1/nib/submissions/${submissionId}/merge-payroll`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return handleApiResponse(response);
  },

  // Add approved new hires after merge
  addNewHires: async (
    submissionId: string,
    newHires: NIBNewHire[],
    addToRoster: boolean = false
  ): Promise<{ records_added: number; added_to_roster: boolean }> => {
    const response = await apiClient.post<
      ApiResponse<{ records_added: number; added_to_roster: boolean }>
    >(`/v1/nib/submissions/${submissionId}/new-hires`, {
      new_hires: newHires,
      add_to_roster: addToRoster,
    });
    return handleApiResponse(response);
  },

  // ==================== Validation & Generation ====================

  // Validate all records in a submission
  validateSubmission: async (
    submissionId: string
  ): Promise<NIBValidationResult> => {
    const response = await apiClient.post<ApiResponse<NIBValidationResult>>(
      `/v1/nib/submissions/${submissionId}/validate`
    );
    return handleApiResponse(response);
  },

  // Generate C10 file from validated submission
  generateC10: async (submissionId: string): Promise<NIBSubmission> => {
    const response = await apiClient.post<ApiResponse<NIBSubmission>>(
      `/v1/nib/submissions/${submissionId}/generate`
    );
    return handleApiResponse(response);
  },

  // Download the generated C10 file
  downloadC10: async (submissionId: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      `/v1/nib/submissions/${submissionId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response as unknown as Blob;
  },

  // ==================== Preview / Calculator ====================

  // Preview contribution calculation for given wages
  previewContribution: async (
    data: NIBContributionPreviewRequest
  ): Promise<NIBContributionPreview> => {
    const response = await apiClient.post<ApiResponse<NIBContributionPreview>>(
      '/v1/nib/preview/contribution',
      data
    );
    return handleApiResponse(response);
  },

  // Preview wage processing (truncation and capping)
  previewWages: async (
    data: NIBWagePreviewRequest
  ): Promise<NIBWagePreview> => {
    const response = await apiClient.post<ApiResponse<NIBWagePreview>>(
      '/v1/nib/preview/wages',
      data
    );
    return handleApiResponse(response);
  },

  // ==================== Utility Methods ====================

  // Get supported source systems for NIB import
  getSupportedSystems: async (): Promise<NIBSourceSystem[]> => {
    const response =
      await apiClient.get<ApiResponse<NIBSourceSystem[]>>('/v1/nib/systems');
    return handleApiResponse(response);
  },

  // Duplicate an existing submission (creates new draft)
  duplicateSubmission: async (submissionId: string): Promise<NIBSubmission> => {
    const response = await apiClient.post<ApiResponse<NIBSubmission>>(
      `/v1/nib/submissions/${submissionId}/duplicate`
    );
    return handleApiResponse(response);
  },
};

export default nibApi;
